/**
 * The ok / warn / critical marker, used anywhere a detector verdict is shown.
 *
 * THE WORD IS PART OF THE COMPONENT, not decoration beside it. This output gets
 * screenshotted into messages and read by people who are not looking closely,
 * and colour alone carries nothing for a red-green colour-blind operator or in
 * a black-and-white paste. Same reasoning as `ops-alerts.command` printing
 * "OK / WARN / !!" as text rather than ANSI colour.
 *
 * `critical` is the only variant that gets a ring. A page where every row
 * shouts is a page where nothing does.
 */
import type { OpsAlertStatus } from '@/types/api';

const styles: Record<OpsAlertStatus, string> = {
  ok: 'bg-brand-green/10 text-brand-green border-brand-green/25',
  warn: 'bg-brand-gold/10 text-brand-gold border-brand-gold/30',
  // Between warn and critical (0054: a bet pending 36 h past kickoff). Orange, no ring.
  error: 'bg-orange-500/10 text-orange-300 border-orange-400/30',
  critical: 'bg-red-500/15 text-red-300 border-red-400/40 ring-1 ring-red-400/20',
};

const labels: Record<OpsAlertStatus, string> = {
  ok: 'OK',
  warn: 'WARN',
  error: 'ERROR',
  critical: 'CRITICAL',
};

export function StatusPill({
  status,
  className = '',
}: {
  status: OpsAlertStatus;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] tabular-nums ${styles[status]} ${className}`}
    >
      {labels[status]}
    </span>
  );
}

/**
 * The neutral cousin, for labels that are states rather than verdicts — a KYC
 * status, an account status, a role. Deliberately a DIFFERENT component rather
 * than a `tone` prop on StatusPill: conflating them is how a `pending` KYC ends
 * up rendered in the same red an operator has learned means "money is moving
 * wrongly right now".
 */
export function Pill({
  children,
  tone = 'neutral',
  className = '',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'positive' | 'caution' | 'muted';
  className?: string;
}) {
  const toneClass = {
    neutral: 'bg-white/[0.06] text-white/70 border-white/15',
    positive: 'bg-brand-teal/10 text-brand-teal border-brand-teal/25',
    caution: 'bg-brand-gold/10 text-brand-gold border-brand-gold/30',
    muted: 'bg-white/[0.03] text-white/40 border-white/10',
  }[tone];

  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${toneClass} ${className}`}
    >
      {children}
    </span>
  );
}
